import {
  TablePreferences,
  tablePreferencesSchema,
} from '@/components/table/preferences/tablePreferences.ts';

/**
 * Abstraction over where per-table column preferences are persisted.
 *
 * Currently backed by `localStorage`. The async signature is intentional so
 * the implementation can later be swapped for a Firestore-backed one without
 * touching any call site.
 */
export interface TablePreferencesStorage {
  load(tableKey: string): Promise<TablePreferences | null>;
  save(tableKey: string, preferences: TablePreferences): Promise<void>;
  remove(tableKey: string): Promise<void>;
}

const STORAGE_PREFIX = 'campdesk.table-preferences.';

class LocalStorageTablePreferencesStorage implements TablePreferencesStorage {
  async load(tableKey: string): Promise<TablePreferences | null> {
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + tableKey);
      if (!raw) return null;
      const parsed = tablePreferencesSchema.safeParse(JSON.parse(raw));
      return parsed.success ? parsed.data : null;
    } catch {
      // unparseable JSON / storage unavailable — treat as no preferences
      return null;
    }
  }

  async save(tableKey: string, preferences: TablePreferences): Promise<void> {
    try {
      localStorage.setItem(
        STORAGE_PREFIX + tableKey,
        JSON.stringify(preferences),
      );
    } catch {
      // storage may be unavailable (private mode / quota) — preferences are non-critical
    }
  }

  async remove(tableKey: string): Promise<void> {
    try {
      localStorage.removeItem(STORAGE_PREFIX + tableKey);
    } catch {
      // ignore
    }
  }
}

/**
 * Singleton used by every table. To migrate persistence to Firestore, provide
 * a class implementing {@link TablePreferencesStorage} and swap it in here.
 */
export const tablePreferencesStorage: TablePreferencesStorage =
  new LocalStorageTablePreferencesStorage();
